import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getExpeditionTileStatus, scanExpeditionArea } from '../api/expeditionsApi';
import { useOptionalAppSession } from '../hooks/AppSessionContext';
import { useGamification } from '../hooks/GamificationContext';
import { usePlayerLocation } from '../game-map/PlayerLocationContext';

function normalizeCoord(value) {
  if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) {
    return null;
  }

  return Math.round(Number(value) * 10000000) / 10000000;
}

function hasCoords(position) {
  return normalizeCoord(position?.latitude) !== null && normalizeCoord(position?.longitude) !== null;
}

function expeditionTileKey(latitude, longitude) {
  const lat = normalizeCoord(latitude);
  const lng = normalizeCoord(longitude);

  if (lat === null || lng === null) return '';

  const latKey = Math.floor((lat + 90) * 1000);
  const lngKey = Math.floor((lng + 180) * 1000);

  return `t${latKey}_${lngKey}`;
}

function distanceMeters(a, b) {
  if (!hasCoords(a) || !hasCoords(b)) return null;

  const earthRadius = 6371000;
  const lat1 = Number(a.latitude) * Math.PI / 180;
  const lat2 = Number(b.latitude) * Math.PI / 180;
  const deltaLat = (Number(b.latitude) - Number(a.latitude)) * Math.PI / 180;
  const deltaLng = (Number(b.longitude) - Number(a.longitude)) * Math.PI / 180;
  const sinLat = Math.sin(deltaLat / 2);
  const sinLng = Math.sin(deltaLng / 2);
  const value = Math.min(1, (sinLat * sinLat) + Math.cos(lat1) * Math.cos(lat2) * (sinLng * sinLng));

  return earthRadius * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));
}

function getHashPath() {
  return String(window.location.hash || '')
    .replace(/^#\/?/, '')
    .split('?')[0]
    .replace(/^\/+|\/+$/g, '');
}

function getCooldownLeftSeconds(state = {}) {
  const cooldownUntil = state.cooldown_until ? new Date(state.cooldown_until).getTime() : 0;

  if (Number.isFinite(cooldownUntil) && cooldownUntil > Date.now()) {
    return Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));
  }

  return Math.max(0, Number(state.cooldown_left_seconds || 0));
}

export default function ExpeditionLivePrompt() {
  const gamification = useGamification() || {};
  const playerLocation = usePlayerLocation();
  const sessionContext = useOptionalAppSession();
  const mergeSessionPatch = sessionContext?.mergeSessionPatch;
  const [currentPath, setCurrentPath] = useState(() => getHashPath());
  const currentPosition = playerLocation.position;
  const [tileStatus, setTileStatus] = useState(null);
  const [dismissedTileKey, setDismissedTileKey] = useState('');
  const [scanning, setScanning] = useState(false);
  const lastRequestedTileKeyRef = useRef('');

  useEffect(() => {
    const handleHashChange = () => setCurrentPath(getHashPath());
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const currentTileKey = hasCoords(currentPosition)
    ? expeditionTileKey(currentPosition.latitude, currentPosition.longitude)
    : '';
  const isOnExpeditionPage = currentPath === 'expeditions';

  useEffect(() => {
    if (isOnExpeditionPage || !currentTileKey || !hasCoords(currentPosition)) {
      if (isOnExpeditionPage) lastRequestedTileKeyRef.current = '';
      setTileStatus(null);
      return undefined;
    }
    if (lastRequestedTileKeyRef.current === currentTileKey) return undefined;

    let cancelled = false;
    const controller = new AbortController();
    const requestedPosition = currentPosition;
    lastRequestedTileKeyRef.current = currentTileKey;
    const timer = window.setTimeout(() => {
      getExpeditionTileStatus(requestedPosition, { signal: controller.signal, compact: true })
        .then((result) => {
          if (cancelled) return;
          setTileStatus({
            tile_key: result?.tile_key || currentTileKey,
            discovered: Boolean(result?.discovered),
            state: result?.state || null,
          });
        })
        .catch(() => {
          if (!cancelled) setTileStatus(null);
        });
    }, 500);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      controller.abort();
      if (lastRequestedTileKeyRef.current === currentTileKey) lastRequestedTileKeyRef.current = '';
    };
  }, [currentTileKey, isOnExpeditionPage]);

  const availableScans = Number(tileStatus?.state?.available_scans || 0);
  const cooldownLeft = getCooldownLeftSeconds(tileStatus?.state || {});
  const canScan = availableScans > 0 && cooldownLeft <= 0 && !scanning;
  const isVisible = Boolean(
    !isOnExpeditionPage
      && currentTileKey
      && tileStatus?.tile_key === currentTileKey
      && tileStatus.discovered === false
      && canScan
      && dismissedTileKey !== currentTileKey
  );

  const handleStart = useCallback(async () => {
    if (!canScan || !hasCoords(currentPosition)) return;

    setScanning(true);
    setDismissedTileKey(currentTileKey);

    try {
      const result = await scanExpeditionArea({ ...currentPosition, available: true });
      if (result?.session_patch && mergeSessionPatch) {
        mergeSessionPatch(result.session_patch);
      } else if (mergeSessionPatch) {
        mergeSessionPatch({ expeditions: result || null });
      }

      if (typeof gamification.applyGamificationPayload === 'function') {
        gamification.applyGamificationPayload(result, {
          emitXpToast: true,
          event: result?.event || 'expedition_scan',
          awarded: Number(result?.awarded || 0),
          toastLabel: result?.xp_toast?.label || 'Expedition abgeschlossen',
        });
      }

      setTileStatus((prev) => ({
        ...(prev || {}),
        tile_key: currentTileKey,
        discovered: true,
      }));
      window.location.hash = '#/expeditions';
    } catch (error) {
      setDismissedTileKey('');
    } finally {
      setScanning(false);
    }
  }, [canScan, currentPosition, currentTileKey, gamification, mergeSessionPatch]);

  const accuracyText = Number(currentPosition?.accuracy || 0) > 0
    ? ` - GPS ca. ${Math.round(Number(currentPosition.accuracy))} m`
    : '';

  if (!isVisible) return null;

  return (
    <aside className="pfp-expedition-live-toast" role="status" aria-live="polite">
      <div className="pfp-expedition-live-toast__body">
        <strong>Neues Gebiet voraus</strong>
        <span>Diese Kachel ist noch unentdeckt. Scanner bereit: {availableScans}{accuracyText}.</span>
      </div>
      <div className="pfp-expedition-live-toast__actions">
        <button type="button" onClick={handleStart} disabled={!canScan}>Expedition starten</button>
        <button type="button" className="is-ghost" onClick={() => setDismissedTileKey(currentTileKey)}>Nicht jetzt</button>
      </div>
    </aside>
  );
}
